// Library to wrap calls to the backend (netlify functions)
// We could think of clever way to auto-generate this file from the backend function declarations

import axios from "redaxios";
import {HostedPage, PortalSession} from "chargebee-typescript/lib/resources";

import {type Language, type ScoreExtension} from "@/shared/constants";
import {type BillingPeriod, type PlanName} from "@/shared/plans";
import Session from "@/stores/Session";
import StageStore from "@/stores/StageStore";

export function backendHeaders() {
  return {...Session.authHeaders, ...StageStore.httpHeaders, "Content-Type": "application/json"};
}

async function callBackend<ResponseData>(func: string, data?: any): Promise<ResponseData> {
  const response = await axios.post<ResponseData>(`/.netlify/functions/${func}`, data, {headers: backendHeaders()});
  return response.data;
}

export async function addPieceFromCatalog(data: {ensembleId: number; pieceId: number}) {
  return await callBackend<{message: string; pieceId: number}>("add-piece-from-catalog", data);
}

export async function buyCredits(data: {credits: number}) {
  return await callBackend<void>("buy-credits", data);
}

export async function cancelTrial() {
  return await callBackend<void>("cancel-trial", {});
}

export async function createPortalSession() {
  return await callBackend<PortalSession>("create-portal-session", {});
}

export async function getCheckoutPage(data: {
  name: string;
  plan: PlanName;
  billingPeriod: BillingPeriod;
  extraSingers?: number;
}) {
  return await callBackend<HostedPage>("get-checkout-page", data);
}

export async function getCheckoutExistingPage(data: {plan: PlanName; billingPeriod: BillingPeriod}) {
  return await callBackend<HostedPage>("get-checkout-existing-page", data);
}

export async function getPriceEstimate(data: {newExtraSingersValue: number; subscriptionId: string}) {
  return await callBackend<{proratedPriceForExtraSingers: number}>("get-price-estimate", data);
}

export async function importUser() {
  return await callBackend<{message: string}>("import-user");
}

export async function inviteUser(data: {
  ensembleId: number;
  ensembleName: string;
  email: string;
  inviterName: string;
  role: string;
}) {
  return await callBackend<{message: string}>("invite-user", data);
}

export async function signUp(data: {name: string; email: string; password: string; confirmSuccessUrl: string}) {
  return await callBackend<void>("sign-up", data);
}

export async function signUpWithInvitation(data: {
  name: string;
  password: string;
  invitationId: string;
  confirmSuccessUrl: string;
}) {
  return await callBackend<void>("sign-up-with-invitation", data);
}

export async function startTrial() {
  return await callBackend<void>("start-trial", {});
}

export async function updateSubscription(data: {
  action: "add" | "remove";
  newExtraSingersValue: number;
  subscriptionId: string;
}) {
  return await callBackend<void>("update-subscription", data);
}

export async function vocantamusCreate(data: {
  ensembleId: number;
  configuration: string;
  language: string;
  scoreExtension: ScoreExtension;
  versionedPieceId: number | null;
}) {
  return await callBackend<{scoreUploadUrl: string; scoreUrl: string; vocantamusTaskId: string}>(
    "vocantamus-create",
    data,
  );
}

export async function vocantamusStart(data: {
  bars: number;
  ensembleId: number;
  folder: string | null;
  composer: string;
  language: Language;
  scoreUrl: string;
  title: string;
  versionedPieceId: number | null;
  vocantamusTaskId: string;
}) {
  return await callBackend<void>("vocantamus-start", data);
}
